import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import ArenaGamePage from '../../TcgArena';
import type { ArenaGuestStatus } from '../../TcgArena/ArenaGamePage';

/* The playable taster on the public TCG page.
 *
 * This is the REAL Duel Arena on the REAL game server: the member arena
 * (ArenaGamePage) in its guest mode — an anonymous session that can do exactly one
 * thing, play a practice match against the server's bot, on the engine members play.
 * A separate, simpler game written for this page would teach the wrong thing about
 * the one they are being asked to sign up for, and so would an offline copy of it.
 *
 * HEAD, not the promoted stable snapshot: only HEAD renders a match the server runs
 * (the stable build plays a local bot, on rules that are on their way out).
 *
 * The frame loads with the page, so the arena is downloaded and warm before anyone
 * decides anything — but no session is minted, no socket opened and nothing dealt
 * until asked. A match left running behind the invitation would spend its shot clock
 * on somebody still reading, and a page view is not a game (the session endpoint is
 * limited per IP).
 *
 * Guest mode does the work inside the frame (see public/tcg-arena/index.html), and
 * ArenaGamePage holds the session and the socket. This component owns the moments
 * the game has no business knowing about: asking whether they want a match, saying
 * so when the demo cannot be reached, and what to offer them when it ends.
 */

const mono = 'ui-monospace, Menlo, Consolas, monospace';
const CYAN = '#38e8ff';
const GOLD = '#ffcf40';
const DIM = '#8ea3b3';

/* How long "Dealing…" may take — minting the session, then the game server's
 * handshake — before the demo says it cannot reach the game. */
const CONNECT_TIMEOUT_MS = 15_000;

type Props = { packValue: string };
type Phase = 'ask' | 'connecting' | 'playing' | 'over' | 'unavailable';
type Outcome = 'you' | 'opp' | 'draw';
type Why = 'busy' | 'maintenance' | 'network' | 'error';

const UNAVAILABLE: Record<Why, { label: string; title: string }> = {
  busy: { label: 'DEMO BUSY', title: 'The demo is busy right now.' },
  maintenance: { label: 'DOWN FOR MAINTENANCE', title: 'The game is down for maintenance.' },
  network: { label: 'CANNOT CONNECT', title: 'We couldn’t reach the game server.' },
  error: { label: 'DEMO UNAVAILABLE', title: 'The demo couldn’t start a match.' }
};

export default function ArenaDemo({ packValue }: Props) {
  // The arena's frame is ArenaGamePage's; it is found in here (it renders exactly one).
  const boxRef = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<Phase>('ask');
  const [outcome, setOutcome] = useState<Outcome>('opp');
  const [ready, setReady] = useState(false);          // the arena has booted (INCRYPT_TCG_READY)
  const [connected, setConnected] = useState(false);  // the guest session is up (session:ready)
  const [connectAsk, setConnectAsk] = useState(0);    // ArenaGamePage's guestConnect
  const [why, setWhy] = useState<{ reason: Why; retryAfterSec?: number }>({ reason: 'error' });
  /* An ask to play that arrived before the game could hear it — the frame still
     booting, or the session still being made. Fired once both are up instead of
     dropped — otherwise the button does nothing and there is no way to tell, from
     the outside, that it was a timing problem rather than a dead control. */
  const wantsPlay = useRef(false);

  const frameWindow = useCallback(
    () => boxRef.current?.querySelector('iframe')?.contentWindow ?? null,
    []
  );

  const tellFrameToPlay = useCallback(() => {
    const w = frameWindow();
    if (!w) return false;
    w.postMessage({ type: 'INCRYPT_TCG_GUEST_PLAY', version: 1 }, window.location.origin);
    return true;
  }, [frameWindow]);

  const unavailable = useCallback((reason: Why, retryAfterSec?: number) => {
    wantsPlay.current = false;
    setWhy({ reason, retryAfterSec });
    setPhase('unavailable');
  }, []);

  useEffect(() => {
    const onMessage = (ev: MessageEvent) => {
      // Only our own frame, and only our own origin — the arena is same-origin here.
      if (!ev.source || ev.source !== frameWindow()) return;
      if (ev.origin !== window.location.origin) return;
      const data = ev.data as { type?: string; won?: boolean; outcome?: string; code?: string } | null;
      if (!data || typeof data.type !== 'string') return;
      if (data.type === 'INCRYPT_TCG_READY') setReady(true);
      if (data.type === 'INCRYPT_TCG_GUEST_RESULT') {
        setOutcome(data.won ? 'you' : data.outcome === 'draw' ? 'draw' : 'opp');
        setPhase('over');
      }
      // the server would not start a match: at capacity (GUEST_BUSY), or not answering
      if (data.type === 'INCRYPT_TCG_GUEST_UNAVAILABLE') {
        unavailable(data.code === 'GUEST_BUSY' ? 'busy' : data.code === 'REQUEST_TIMEOUT' ? 'network' : 'error');
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [frameWindow, unavailable]);

  const onGuestStatus = useCallback(
    (s: ArenaGuestStatus) => {
      if (s.type === 'connected') setConnected(true);
      else if (s.type === 'disconnected') setConnected(false);
      else unavailable(s.reason, s.retryAfterSec);   // the session could not be minted
    },
    [unavailable]
  );

  // The ask, delivered the moment the game can hear it.
  useEffect(() => {
    if (!wantsPlay.current || !ready || !connected) return;
    if (tellFrameToPlay()) {
      wantsPlay.current = false;
      setPhase('playing');
    }
  }, [ready, connected, tellFrameToPlay]);

  // Asked, and nothing: the session or the socket never came up.
  useEffect(() => {
    if (phase !== 'connecting') return;
    const t = window.setTimeout(() => unavailable('network'), CONNECT_TIMEOUT_MS);
    return () => window.clearTimeout(t);
  }, [phase, unavailable]);

  /* Play, play again, and try again are one act: deal now if the session is up,
     otherwise ask ArenaGamePage for one (its first mint, or another after a refusal)
     and deal when it lands. */
  const play = useCallback(() => {
    if (ready && connected && tellFrameToPlay()) {
      setPhase('playing');
      return;
    }
    wantsPlay.current = true;
    setPhase('connecting');
    setConnectAsk((n) => n + 1);
  }, [ready, connected, tellFrameToPlay]);

  const won = outcome === 'you';

  return (
    <Box>
      <Box sx={{ textAlign: 'center', mb: { xs: 2.5, md: 3.5 } }}>
        <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: { xs: 23, md: 30 } }}>
          Try our Trading Card Game
        </Typography>
        <Typography
          sx={{ mt: 1.25, mx: 'auto', maxWidth: 580, color: '#c7d6e0', fontSize: 15.5, lineHeight: 1.6 }}
        >
          No account, no download — this is the real TCG members play, dealt with a deck
          built for you. Three lanes, 500 life points, last one standing.
        </Typography>
      </Box>

      <Box
          ref={boxRef}
          sx={{
            position: 'relative',
            borderRadius: '18px',
            overflow: 'hidden',
            border: '1px solid rgba(56,232,255,0.28)',
            boxShadow: '0 30px 80px -40px rgba(56,232,255,0.5), 0 20px 60px -30px rgba(0,0,0,0.9)',
            background: '#05080d',
            /* The arena lays itself out to its own viewport, so the frame has to be
               a real box rather than something that grows with content. Tall enough
               on a phone to leave the board and the hand both legible. */
            height: { xs: 'min(150vw, 620px)', sm: 'min(72vh, 640px)', md: 'min(78vh, 760px)' }
          }}
        >
          {/* The promoted stable build, as /play loads it — never HEAD: every website visitor sees this */}
          <ArenaGamePage
            embedded
            guest
            requireAdmin={false}
            arenaUrl="/tcg-arena/stable/index.html"
            guestConnect={connectAsk}
            onGuestStatus={onGuestStatus}
          />

          {(phase === 'ask' || phase === 'connecting') && (
            <Panel
              label={phase === 'connecting' ? 'DEALING' : ready ? 'READY WHEN YOU ARE' : 'LOADING THE GAME'}
              tint={CYAN}
            >
              <Typography
                component="h3"
                sx={{ mt: 1.25, color: '#fff', fontWeight: 900, fontSize: { xs: 21, md: 26 }, lineHeight: 1.2 }}
              >
                Want to try our Trading Card Game?
              </Typography>
              <Typography sx={{ mt: 1.5, color: '#c7d6e0', fontSize: 14.5, lineHeight: 1.55 }}>
                One match, about three minutes, right in your browser.
              </Typography>
              <Button
                onClick={play}
                disabled={!ready || phase === 'connecting'}
                sx={{ ...ctaSx, mt: 2.5, width: '100%' }}
              >
                {ready && phase === 'ask' ? "Yes, let's play" : 'Dealing…'}
              </Button>
            </Panel>
          )}

          {phase === 'unavailable' && (
            <Panel label={UNAVAILABLE[why.reason].label} tint={DIM}>
              <Typography
                component="h3"
                sx={{ mt: 1.25, color: '#fff', fontWeight: 900, fontSize: { xs: 21, md: 26 }, lineHeight: 1.2 }}
              >
                {UNAVAILABLE[why.reason].title}
              </Typography>
              <Typography sx={{ mt: 1.5, color: '#c7d6e0', fontSize: 14.5, lineHeight: 1.55 }}>
                {why.retryAfterSec
                  ? `Try again in about ${
                      why.retryAfterSec >= 90 ? `${Math.ceil(why.retryAfterSec / 60)} minutes` : `${why.retryAfterSec} seconds`
                    }.`
                  : 'Give it a moment, then try again.'}
              </Typography>
              <Button onClick={play} sx={{ ...ctaSx, mt: 2.5, width: '100%' }}>
                Try again
              </Button>
            </Panel>
          )}

          {phase === 'over' && (
            <Panel label={won ? 'VICTORY' : outcome === 'draw' ? 'DRAW' : 'DEFEAT'} tint={won ? GOLD : DIM} gold>
              <Typography
                component="h3"
                sx={{ mt: 1.25, color: '#fff', fontWeight: 900, fontSize: { xs: 21, md: 26 }, lineHeight: 1.2 }}
              >
                Sign up now for a free Relic Starter Pack
              </Typography>
              <Box
                sx={{ mt: 1.25, display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 1.25 }}
              >
                <Typography
                  sx={{
                    color: DIM,
                    textDecoration: 'line-through',
                    textDecorationColor: 'rgba(255,255,255,0.4)',
                    fontSize: 18,
                    fontWeight: 600
                  }}
                >
                  valued at {packValue}
                </Typography>
                <Typography sx={{ color: GOLD, fontSize: 24, fontWeight: 900, lineHeight: 1 }}>
                  FREE
                </Typography>
              </Box>
              <Typography sx={{ mt: 1.5, color: '#c7d6e0', fontSize: 14.5, lineHeight: 1.55 }}>
                {won
                  ? 'That was a prebuilt deck. Sign up for a free Relic pack and the cards are yours — build your own deck, trade on the marketplace, and take it to other members.'
                  : 'Bad luck. Sign up for a free Relic pack, build a deck of your own, and come back for another go at the bot — and at other members.'}
              </Typography>
              <Button component={Link} to="/sign-up" sx={{ ...ctaSx, mt: 2.5, width: '100%' }}>
                Create your free account
              </Button>
              <Button onClick={play} sx={{ ...ghostSx, mt: 1.25 }}>
                Play again
              </Button>
              <Typography sx={{ mt: 1.75, color: DIM, font: `600 11.5px ${mono}` }}>
                Free for verified members · one per account
              </Typography>
            </Panel>
          )}
      </Box>
    </Box>
  );
}

/* Every moment the host owns looks the same, because they are the same thing: the
 * page speaking over a board it has paused. Inside the frame's box rather than over
 * the whole page — the arena stays visible behind them, which is the point. */
function Panel({
  label,
  tint,
  gold = false,
  children
}: {
  label: string;
  tint: string;
  gold?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Box
      role="dialog"
      aria-modal="true"
      aria-label={label}
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'grid',
        placeItems: 'center',
        p: 2,
        background: 'rgba(4,8,13,0.88)',
        backdropFilter: 'blur(6px)'
      }}
    >
      <Box
        sx={{
          maxWidth: 430,
          width: '100%',
          maxHeight: '100%',
          overflowY: 'auto',
          textAlign: 'center',
          px: { xs: 2.5, md: 4 },
          py: { xs: 3, md: 4 },
          borderRadius: '18px',
          border: `1px solid ${gold ? 'rgba(255,207,64,0.4)' : 'rgba(56,232,255,0.32)'}`,
          background: gold
            ? 'linear-gradient(160deg, rgba(24,20,10,0.98), rgba(9,14,21,0.99))'
            : 'linear-gradient(160deg, rgba(10,22,30,0.98), rgba(9,14,21,0.99))',
          boxShadow: gold
            ? '0 40px 100px -40px rgba(255,207,64,0.5)'
            : '0 40px 100px -40px rgba(56,232,255,0.45)'
        }}
      >
        <Typography sx={{ font: `800 11px ${mono}`, letterSpacing: '0.3em', color: tint }}>
          {label}
        </Typography>
        {children}
      </Box>
    </Box>
  );
}

const ctaSx = {
  px: 4,
  py: 1.15,
  borderRadius: 999,
  fontWeight: 900,
  fontSize: 15,
  textTransform: 'none' as const,
  color: '#04121a',
  background: 'linear-gradient(180deg,#ffdd6e,#ffcf40)',
  boxShadow: '0 14px 34px -12px rgba(255,207,64,0.7)',
  '&:hover': { background: 'linear-gradient(180deg,#ffe790,#ffd85c)' },
  '&.Mui-disabled': { background: 'rgba(255,207,64,0.28)', color: 'rgba(4,18,26,0.6)' }
};

const ghostSx = {
  width: '100%',
  py: 1,
  borderRadius: 999,
  textTransform: 'none' as const,
  fontWeight: 800,
  fontSize: 14.5,
  color: CYAN,
  border: `1px solid ${CYAN}55`,
  '&:hover': { background: `${CYAN}12` }
};
